import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export const useBadgeAPI = () => {
  const { toast } = useToast();
  const [isSupported, setIsSupported] = useState(false);
  const [currentBadgeCount, setCurrentBadgeCount] = useState(0);

  // Check if Badge API is supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
      console.log('Badge API supported:', supported);
      setIsSupported(supported);
      
      // Don't show error toast immediately - let users try the functionality
      if (!supported) {
        console.warn('Badge API not supported in this browser');
      }
    };
    
    checkSupport();
  }, [toast]);

  // Set badge count on app icon
  const setBadgeCount = useCallback(async (count: number) => {
    // Check support dynamically to avoid race conditions
    const runtimeSupported = 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
    console.log('Setting badge count:', count, 'Runtime supported:', runtimeSupported);
    
    if (!runtimeSupported) {
      console.warn('Badge API not supported at runtime, cannot set badge count');
      // Only show toast if user is explicitly trying to set badge
      if (count > 0) {
        toast({
          title: 'Badge API չի աջակցվում',
          description: 'Badge-ները չեն աջակցվում այս սարքում։ Մոբայլ սարքերում ապպը պետք է տեղադրված լինի իբրև PWA։',
          variant: 'destructive',
        });
      }
      
      // Try service worker fallback for PWA
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          registration.active?.postMessage({
            type: 'SET_BADGE_COUNT',
            count: count
          });
          console.log('Badge count sent to service worker as fallback');
          setCurrentBadgeCount(count);
          return true;
        } catch (swError) {
          console.error('Service worker fallback failed:', swError);
        }
      }
      return false;
    }

    try {
      if (count > 0) {
        console.log('Setting app badge to:', count);
        await navigator.setAppBadge(count);
        console.log('✅ App badge set successfully to:', count);
      } else {
        console.log('Clearing app badge');
        await navigator.clearAppBadge();
        console.log('✅ App badge cleared successfully');
      }
      setCurrentBadgeCount(count);
      
      // Also sync with service worker for consistency
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          registration.active?.postMessage({
            type: 'SET_BADGE_COUNT',
            count: count
          });
          console.log('Badge count synced with service worker');
        } catch (swError) {
          console.error('Error syncing badge with service worker:', swError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error setting app badge:', error);
      
      // Provide specific guidance for common issues
      let errorMessage = `Չհաջողվեց թարմացնել badge-ը: ${error.message}`;
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Badge-ը արգելված է։ Մոբայլ սարքերում ապպը պետք է տեղադրված լինի իբրև PWA "Add to Home Screen" միջոցով։';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Badge API-ն չի աջակցվում այս սարքում կամ browser-ում։';
      }
      
      toast({
        title: 'Badge սխալ',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  // Clear badge
  const clearBadge = useCallback(async () => {
    return await setBadgeCount(0);
  }, [setBadgeCount]);

  // Increment badge count
  const incrementBadge = useCallback(async () => {
    const newCount = currentBadgeCount + 1;
    return await setBadgeCount(newCount);
  }, [currentBadgeCount, setBadgeCount]);

  // Decrement badge count
  const decrementBadge = useCallback(async () => {
    const newCount = Math.max(0, currentBadgeCount - 1);
    return await setBadgeCount(newCount);
  }, [currentBadgeCount, setBadgeCount]);

  return {
    isSupported,
    currentBadgeCount,
    setBadgeCount,
    clearBadge,
    incrementBadge,
    decrementBadge,
  };
};